import React from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { sharedModeNoticeAppliesToZone, useSharedModeNoticeStore } from '../utils/sharedModeNotice';
import { stickySharedMarkFor, useStickySharedStore } from '../utils/stickySharedFallback';
import { isWindowsConnectorHost } from '../utils/platform';
import { useMaestroStore } from '../store/maestroStore';

const NOTICE_STYLE = {
  fontSize: 10,
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 4,
  color: '#B7791F',
  background: 'rgba(214, 158, 46, 0.12)',
  border: '1px solid rgba(214, 158, 46, 0.55)',
  whiteSpace: 'nowrap',
  cursor: 'help',
  letterSpacing: '0.02em',
};

/**
 * #10426-a1 (MST-6431) — persistent label while a bit-perfect (local-native) zone plays in shared
 * mode: after a fallback, or from the first moment of every play while the device is sticky-shared.
 * Shared audio goes to the computer's selected sound output — the tooltip says so, never the DAC.
 */
export default function SharedModeNoticeBadge({ zone, className = '' }) {
  const { t } = useTranslation();
  const notice = useSharedModeNoticeStore((s) => s.notice);
  const marks = useStickySharedStore((s) => s.marks);
  const fromNotice = sharedModeNoticeAppliesToZone(notice, zone);
  const usbOutputPath = useMaestroStore((s) => s.usbOutputPath);
  // Sticky marks PCM only — a DSD track that opened bit-perfect on the same device is not shared.
  const sticky = usbOutputPath !== 'exclusive' && stickySharedMarkFor(marks, zone);
  if (!fromNotice && !sticky) return null;
  const connectorMessage = fromNotice && notice.reason === 'connector_shared' ? notice.message : null;
  const detail = `${connectorMessage
    || t(isWindowsConnectorHost() ? 'exclusive.shared_mode_pc_output' : 'exclusive.shared_mode_mac_output')} ${t('exclusive.shared_mode_retry_hint')}`;

  return (
    <span
      role="status"
      className={className}
      title={detail}
      style={NOTICE_STYLE}
      data-shared-mode-notice={fromNotice ? notice.reason : 'sticky_shared'}
    >
      {t('player.system_audio_shared_tag')}
    </span>
  );
}
